import { useEffect } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { toggleLensUndoWithToast } from '~/hooks/apis/reporting/lensUndoToast';
import { latestLensUndo, lensChangeKey } from '~/hooks/apis/reporting/useLensChange';

const TEXT_ENTRY = 'input, textarea, select, [contenteditable="true"], .cm-editor';

/** Cmd/Ctrl+Z undoes the actor's latest operation on this Lens; Cmd/Ctrl+Shift+Z redoes the latest undone one. */
export function useLensUndoShortcut(lensId: string | undefined, canEdit: boolean) {
  const client = useQueryClient();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const { mutate } = useMutation({
    mutationKey: lensChangeKey(lensId ?? ''),
    mutationFn: (key: ReadonlyArray<string>) => toggleLensUndoWithToast(client, key, { closeSnackbar, enqueueSnackbar }),
  });
  useEffect(() => {
    if (!lensId || !canEdit) return;
    const undoOrRedo = (event: KeyboardEvent) => {
      const isShortcut = event.key.toLowerCase() === 'z' && (event.metaKey || event.ctrlKey);
      if (!isShortcut || (event.target as HTMLElement | null)?.closest?.(TEXT_ENTRY)) return;
      const key = latestLensUndo(client, lensId, event.shiftKey);
      if (!key) return;
      event.preventDefault();
      mutate(key);
    };
    window.addEventListener('keydown', undoOrRedo);
    return () => window.removeEventListener('keydown', undoOrRedo);
  }, [canEdit, client, lensId, mutate]);
}
